// src/pages/customer/components/details/tabs/CustomerAnamneseTab.jsx
import "./CustomerAnamneseTab.scss";

import { Badge, Button } from "react-bootstrap";
import { useEffect, useMemo, useState } from "react";

import AnamneseAPI from "../../../../atendimento/anamnese/api/AnamneseAPI";
import AppLoading from "../../../../../commons/AppLoading";
import { formatDateTime } from "../../../../../commons/util/HallAppUtil";
import { useNavigate } from "react-router-dom";

const anamneseAPI = new AnamneseAPI();

/**
 * Status exemplos (ajuste conforme seu backend):
 * CREATED, SENT, FILLED, CANCELED, EXPIRED, FAILED...
 */
const anamneseStatusLabel = (status) => {
  const s = String(status || "").toUpperCase();
  switch (s) {
    case "CREATED":
      return "Criada";
    case "SENT":
      return "Enviada";
    case "FILLED":
      return "Preenchida";
    case "CANCELED":
      return "Cancelada";
    case "EXPIRED":
      return "Expirada";
    case "FAILED":
      return "Falhou";
    default:
      return status ? String(status) : "—";
  }
};

const anamneseStatusVariant = (status) => {
  const s = String(status || "").toUpperCase();
  switch (s) {
    case "FILLED":
      return "success";
    case "SENT":
      return "primary";
    case "CREATED":
      return "info";
    case "CANCELED":
      return "secondary";
    case "EXPIRED":
      return "dark";
    case "FAILED":
      return "danger";
    default:
      return "dark";
  }
};

const SafeLine = ({ show, children }) => (show ? <div className="hc-an-line">{children}</div> : null);

/**
 * Props sugeridas:
 * - customerId: id do cliente (se seu backend aceitar)
 * - customerWhatsapp: whatsapp do cliente (no seu print, o filtro era por whatsapp)
 * - show: boolean (aba ativa)
 */
export default function CustomerAnamneseTab({
  customerId,
  customerWhatsapp,
  show,
  onOpen, // opcional: (item) => void
}) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");
  const navigate = useNavigate();

  const queryValue = useMemo(() => {
    // Prioriza whatsapp (se existir), senão tenta customerId como fallback
    if (customerWhatsapp) return String(customerWhatsapp);
    if (customerId != null) return String(customerId);
    return "";
  }, [customerWhatsapp, customerId]);

  const loadData = async () => {
    setErrorMsg("");
    setLoading(true);

    try {
      const filter = {
        q: customerWhatsapp,
        page: 0,
        size: 50,
      };

      if (customerWhatsapp) {
        const resp = await anamneseAPI.list(filter);

        const data = resp?.data || {};
        const content = Array.isArray(data?.content) ? data.content : [];
        setItems(content);
      } else {
        setItems([]);
      }


    } catch (e) {
      console.error(e);
      setItems([]);
      setErrorMsg("Não foi possível carregar as fichas de anamnese.");
    } finally {
      setLoading(false);
    }
  }


  useEffect(() => {
    loadData();
  }, []);

  const handleOpen = (it) => {
    navigate(`/anamnese/${it.id}`)
  };

  return (
    <div className="hc-cd-panel">
      <div className="d-flex align-items-center justify-content-between gap-2">
        <div className="hc-cd-panel__title mb-0">Anamnese</div>

        <Button size="sm" variant="outline-dark" onClick={loadData} disabled={loading}>
          Atualizar
        </Button>
      </div>

      {loading ? (
        <div className="mt-3">
          <AppLoading />
        </div>
      ) : errorMsg ? (
        <div className="hc-an-error mt-3">{errorMsg}</div>
      ) : !items || items.length === 0 ? (
        <div className="hc-cd-empty mt-3">
          <div className="hc-cd-empty__title">Nenhuma ficha encontrada</div>
          <div className="hc-cd-empty__subtitle">Esse cliente ainda não possui fichas de anamnese.</div>
        </div>
      ) : (
        <div className="hc-an-list mt-3">
          {items.map((it, idx) => {
            const key = it.id ?? idx;

            const statusText = anamneseStatusLabel(it.status);
            const statusBg = anamneseStatusVariant(it.status);

            const created = it.createDt ? formatDateTime(it.createDt) : null;
            const filled = it.filledDt ? formatDateTime(it.filledDt) : null;

            // dentro do map() do CustomerAnamneseTab.jsx, substitui o return do card por este:

            return (
              <div key={key} className="hc-an-card2">
                <div className="hc-an-card2__head">
                  <div className="hc-an-card2__titleWrap">
                    <div className="hc-an-card2__title text-truncate">
                      {it.templateName || "Ficha de anamnese"}
                    </div>

                    <div className="hc-an-card2__sub text-truncate">
                      {it.customerName ? `Cliente: ${it.customerName}` : "—"}
                    </div>
                  </div>

                  <div className={`hc-an-chip hc-an-chip--${String(statusBg || "dark")}`}>
                    <span className="hc-an-chip__dot" />
                    <span className="hc-an-chip__text">{statusText}</span>
                  </div>
                </div>

                <div className="hc-an-card2__body">
                  <div className="hc-an-pills">
                    {it.whatsapp ? (
                      <div className="hc-an-pill">
                        <span className="hc-an-pill__ico">📱</span>
                        <span className="hc-an-pill__txt">{it.whatsapp}</span>
                      </div>
                    ) : null}

                    {it.description ? (
                      <div className="hc-an-pill hc-an-pill--wide" title={it.description}>
                        <span className="hc-an-pill__ico">📝</span>
                        <span className="hc-an-pill__txt text-truncate">{it.description}</span>
                      </div>
                    ) : null}
                  </div>

                  <div className="hc-an-dates">
                    <div className="hc-an-date">
                      <div className="hc-an-date__label">Criada</div>
                      <div className="hc-an-date__value">{created || "—"}</div>
                    </div>

                    <div className="hc-an-date">
                      <div className="hc-an-date__label">Preenchida</div>
                      <div className="hc-an-date__value">{filled || "—"}</div>
                    </div>
                  </div>
                </div>

                <div className="hc-an-card2__foot">
                  <Button size="sm" variant="outline-dark" className="hc-an-btnGhost" onClick={() => handleOpen(it)}>
                    Abrir
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="hc-cd-panel__hint mt-2">
        As fichas exibidas respeitam o filtro do cliente (whatsapp/id) usado na busca.
      </div>
    </div>
  );
}